import {
  AuthProvider,
  OAuthActionType,
  OAuthErrorResponse,
  OAuthResponse,
  OAuthSuccessResponse,
} from '@avanzu/types';
import GoogleIcon from '@mui/icons-material/Google';
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AvanzuAdminHeader from '../../components/AvanzuAdminHeader';
import { useAuth } from '../../contexts/AuthContext';

interface LoginError {
  error: string;
}

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState<LoginError | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Listen for messages from popup
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      const data = event.data as OAuthResponse;

      // Handle new structured OAuth responses
      if (data && typeof data.success !== 'undefined') {
        if (data.success) {
          // Handle successful OAuth login
          const successData = data as OAuthSuccessResponse;

          if (successData.action === OAuthActionType.LOGIN && successData.token) {
            try {
              setLoginError(null);
              await login(successData.token);
              navigate('/app');
            } catch (error) {
              console.error('Failed to process login:', error);
              setLoginError({
                error: 'Failed to complete login. Please try again.',
              });
            }
          }
        } else {
          // Handle OAuth error
          const errorData = data as OAuthErrorResponse;
          console.error('OAuth error:', errorData.message);
          setLoginError({
            error: errorData.message || 'OAuth login failed. Please try again.',
          });
        }
        return;
      }

      // Keep legacy support for backward compatibility
      if (event.data.type === 'oauth-success' && event.data.token) {
        try {
          setLoginError(null);
          await login(event.data.token);
          navigate('/app');
        } catch (error) {
          console.error('Failed to process login:', error);
          setLoginError({
            error: 'Failed to complete login. Please try again.',
          });
        }
      } else if (event.data.type === 'oauth-error') {
        console.error('OAuth error:', event.data.error);
        setLoginError({
          error: event.data.message || event.data.error || 'OAuth login failed. Please try again.',
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [login, navigate]);

  const handleGoogleLogin = () => {
    setLoginError(null);
    setIsLoading(true);

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      `${process.env.REACT_APP_API_URL}/auth/oauth/${AuthProvider.GOOGLE}?popup=true&action=login&domain=admin`,
      `google-oauth`,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
    );

    if (!popup || popup.closed) {
      setLoginError({
        error: 'Please allow popups for this site to sign in.',
      });
      setIsLoading(false);
      return;
    }

    const checkPopup = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopup);
        setIsLoading(false);
      }
    }, 1000);
  };

  return (
    <Container maxWidth="xs">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ mb: 4 }}>
          <AvanzuAdminHeader />
        </Box>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography variant="h4" align="center" gutterBottom>
            Admin Login
          </Typography>

          <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 2, mb: 3 }}>
            Sign in with your Avanzu Google account
          </Typography>

          {loginError && (
            <Box
              sx={{
                p: 2,
                mb: 2,
                bgcolor: 'error.light',
                color: 'error.contrastText',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2">{loginError.error}</Typography>
            </Box>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleLogin}
            disabled={isLoading}
            sx={{
              bgcolor: '#4285F4',
              '&:hover': { bgcolor: '#357ae8' },
              py: 1.5,
            }}
          >
            {isLoading ? 'Signing in...' : 'Sign in with Google'}
          </Button>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
